import React, { useState, useEffect, useMemo } from 'react';
import { UserProfile, Tenant, Role, UserStatus } from '../../types';
import { CollapsibleSection } from '../CollapsibleSection';
import { BulkUserUpload } from './BulkUserUpload';
import { parseCsvToUsersStrict } from '../../utils/csvUtils';
import { firestoreService } from '../../services/firestoreService';
import { ReportingView } from './ReportingView';

type AdminTab = 'configure' | 'reporting';

const normalizeEmail = (v: unknown) => String(v || '').trim().toLowerCase();
const normalizeDisplayName = (v: unknown) => String(v || '').trim().replace(/\s+/g, ' ');
const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const UserManagement: React.FC<{ tenantId: string; refreshNonce: number }> = ({ tenantId, refreshNonce }) => {

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [invites, setInvites] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const [userList, inviteList] = await Promise.all([
        firestoreService.getUsersForTenant(tenantId),
        firestoreService.getInvitationsForTenant(tenantId),
      ]);

      setUsers((userList || []).sort((a, b) => a.email.localeCompare(b.email)));

      setInvites(
        (inviteList || [])
          .filter((inv: any) => {
            const s = String(inv.status ?? inv.Status ?? inv.inviteStatus ?? inv.state ?? 'ACTIVE')
              .toUpperCase()
              .trim();
            return s === 'PENDING';
          })
          .sort((a: any, b: any) => (a.email || '').localeCompare(b.email || ''))
      );
    } catch {
      alert('Failed to load users/invitations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
  fetchUsers();
}, [tenantId, refreshNonce]);

  const handleStatusToggle = async (user: UserProfile) => {
    const newStatus = user.status === UserStatus.ACTIVE ? UserStatus.INACTIVE : UserStatus.ACTIVE;
    try {
      await firestoreService.updateUserStatus(user.uid, newStatus);
      setUsers(prev => prev.map(u => (u.uid === user.uid ? { ...u, status: newStatus } : u)));
    } catch {
      alert('Failed to update user status.');
    }
  };

  const handleRevokeInvite = async (inviteIdRaw: string) => {
    const inviteId = String(inviteIdRaw || '').trim();
    if (!inviteId) return;

    try {
await firestoreService.revokeInvitationByEmail(inviteId);
      setInvites(prev => prev.filter((inv: any) => String(inv.id || '').trim() !== inviteId));
    } catch {
      alert('Failed to revoke invitation.');
    }
  };

  if (loading) return <p>Loading users...</p>;

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.uid}>
                  <td className="px-4 py-2 text-sm">{user.email}</td>
                  <td className="px-4 py-2 text-sm">
                    <span
                      className={`px-2 inline-flex text-xs font-semibold rounded-full ${
                        user.status === UserStatus.ACTIVE ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-sm text-center">
                    <button onClick={() => handleStatusToggle(user)} className="text-brand-orange hover:underline text-xs">
                      {user.status === UserStatus.ACTIVE ? 'Deactivate' : 'Activate'}
                    </button>
                  </td>
                </tr>
              ))}
              {users.length === 0 && (
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-500" colSpan={3}>
                    No users found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <button onClick={fetchUsers} className="text-xs text-gray-500 hover:underline">
          Refresh
        </button>
      </div>

      <div className="space-y-2">
        <div className="text-sm font-semibold text-brand-grey">Pending Invitations</div>
        <div className="overflow-x-auto bg-white rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {(invites || []).map((inv: any) => (
                <tr key={inv.id || inv.email}>
                  <td className="px-4 py-2 text-sm">{inv.email}</td>
                  <td className="px-4 py-2 text-sm">{inv.role}</td>
                  <td className="px-4 py-2 text-sm">{inv.status}</td>
                  <td className="px-4 py-2 text-sm text-center">
                    <button onClick={() => handleRevokeInvite(inv.id)} className="text-brand-orange hover:underline text-xs">
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
              {(!invites || invites.length === 0) && (
                <tr>
                  <td className="px-4 py-3 text-sm text-gray-500" colSpan={4}>
                    No pending invitations.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const CreateSingleUser: React.FC<{
  tenantId: string;
  createdBy: UserProfile;
  onInviteCreated: () => void;
}> = ({ tenantId, createdBy, onInviteCreated }) => {

  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFeedback(null);

    const normalizedEmail = normalizeEmail(email);
    const name = normalizeDisplayName(displayName);

    if (!normalizedEmail || !isValidEmail(normalizedEmail)) {
      setFeedback({ type: 'error', text: 'Invalid email.' });
      return;
    }
    if (!name) {
      setFeedback({ type: 'error', text: 'displayName is required.' });
      return;
    }

    setSubmitting(true);
    try {
     await firestoreService.createUser(
  { email: normalizedEmail, displayName: name, role: Role.TEACHER_USER, tenantId },
  createdBy
);

onInviteCreated();


setFeedback({ type: 'success', text: `Invitation created for ${normalizedEmail}.` });

      setEmail('');
      setDisplayName('');
    } catch (err: any) {
      setFeedback({ type: 'error', text: err?.message || 'Failed to create invitation.' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={onCreate} className="p-4 space-y-3">
      {feedback && (
        <div className={`p-2 rounded text-sm ${feedback.type === 'success' ? 'bg-green-100' : 'bg-red-100'}`}>
          {feedback.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="md:col-span-1">
          <label className="block text-xs font-semibold text-brand-grey mb-1">Email</label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="block w-full px-3 py-2 border rounded-md"
            placeholder="teacher@school.com"
          />
        </div>

        <div className="md:col-span-2">
          <label className="block text-xs font-semibold text-brand-grey mb-1">Display Name</label>
          <input
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            className="block w-full px-3 py-2 border rounded-md"
            placeholder="Teacher Name"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <button type="submit" disabled={submitting} className="px-4 py-2 bg-brand-orange text-white text-sm font-semibold rounded-md">
          {submitting ? 'Creating...' : 'Create Invitation'}
        </button>
      </div>
    </form>
  );
};

export const TenantAdminView: React.FC<{
  userProfile: UserProfile;
  tenant: Tenant;
  updateTenant: (t: Tenant) => void;
}> = ({ userProfile, tenant }) => {
  const [activeTab, setActiveTab] = useState<AdminTab>('configure');
const [userMgmtRefreshNonce, setUserMgmtRefreshNonce] = useState(0);
const requestUserMgmtRefresh = () => setUserMgmtRefreshNonce(n => n + 1);

  const bulkHeaders = useMemo(() => ['email', 'displayname'], []);

  const processTenantUsers = async (file: File) => {
    const users = await parseCsvToUsersStrict(file, ['email', 'displayname']);

    const normalized = users.map((u: any) => {
      const rawEmail = String(u.email || '');
      const email = rawEmail.trim().toLowerCase();

      const displayName = String((u as any).displayname || '').trim().replace(/\s+/g, ' ');

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        throw new Error(`Row ${u.rowNumber}: Invalid email.`);
      }
      if (!displayName) {
        throw new Error(`Row ${u.rowNumber}: displayName is required.`);
      }

      return { ...u, email, displayName, role: Role.TEACHER_USER, tenantId: tenant.id };
    });

    const seen = new Set<string>();
    const dupes: string[] = [];
    for (const u of normalized) {
      if (seen.has(u.email)) dupes.push(u.email);
      seen.add(u.email);
    }
    if (dupes.length) throw new Error(`CSV has duplicate emails: ${Array.from(new Set(dupes)).join(', ')}`);

    normalized.sort((a: any, b: any) => String(a.email).localeCompare(String(b.email)));

return firestoreService.bulkAddUsers({
  createdByUid: String(userProfile.uid || '').trim(),
  tenantId: tenant.id,
  tenantName: String((tenant as any)?.tenantName || (tenant as any)?.name || '').trim() || null,
  partnerId: String((tenant as any)?.partnerId || userProfile.partnerId || '').trim() || null,
  rows: normalized.map((u: any) => ({
    email: String(u.email || '').trim().toLowerCase(),
    role: Role.TEACHER_USER,
    displayName: String(u.displayName || '').trim() || null,
  })),
});  };

  const renderConfigureTab = () => (
    <div className="space-y-6">
      <CollapsibleSection title="Create Single User" defaultOpen={true}>
      <CreateSingleUser
  tenantId={tenant.id}
  createdBy={userProfile}
  onInviteCreated={requestUserMgmtRefresh}
/>


      </CollapsibleSection>

      <CollapsibleSection title="User Management" defaultOpen={true}>
        <div className="p-4">
        <UserManagement tenantId={tenant.id} refreshNonce={userMgmtRefreshNonce} />

        </div>
      </CollapsibleSection>

     <CollapsibleSection title="Bulk User Upload">
  <BulkUserUpload
    templateHeaders={bulkHeaders}
    templateFilename={`${tenant.id}_teacher_template.csv`}
    processAndUpload={async (file: File) => {
      await processTenantUsers(file);
      requestUserMgmtRefresh();
    }}
  />
</CollapsibleSection>
    </div>
  );

  return (
    <>
      <div className="mb-6 flex border-b-2 border-gray-200">
        {(['configure', 'reporting'] as AdminTab[]).map(tab => (
         <button
  type="button"
  key={tab}
  onClick={() => setActiveTab(tab)}
  className={`px-6 py-2 text-lg font-semibold capitalize transition-colors ${
    activeTab === tab ? 'border-b-2 border-brand-orange text-brand-orange' : 'text-gray-500 hover:text-brand-grey'
  }`}
>

            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'configure' && renderConfigureTab()}
    {activeTab === 'reporting' && <ReportingView />}

    </>
  );
};
